// Order form field definitions, keyed by item type.

// Show the form when the item genuinely needs input before ordering.
// Products only need the form when there are attribute/option choices.
export function needsOrderForm(item) {
  if (!item) return false;
  // Gigs & services always need a form
  if (item.jobType || item.isService) return true;
  // Products only if they have selectable attributes or custom options
  const hasAttrs = (item.attributes || []).some(
    a => a?.name && (a?.values || []).filter(Boolean).length > 0
  );
  const hasOpts = (item.customOptions || []).filter(Boolean).length > 0;
  return hasAttrs || hasOpts;
}

// ── Gig-claim fields (serviceTab === 'need') ────────────────────────────────
// The PRO filling these in is applying to take the job.
function getGigClaimFields(item) {
  const jt = item.jobType;

  if (jt === 'rides') return [
    { key: 'vehicle',      label: 'Your Vehicle',          type: 'text',     placeholder: 'Make · Model · Year · Color…', required: false },
    { key: 'seats',        label: 'Seats Available',       type: 'stepper',  min: 1, max: 15, defaultVal: 4 },
    { key: 'drivingExp',   label: 'Driving Experience',    type: 'select',   options: ['1–2 years', '3–5 years', '5–10 years', '10+ years'], required: false },
    { key: 'licensed',     label: 'Valid Commercial/Rideshare License', type: 'toggle' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'e.g. Weekdays after 3 pm, weekends…', required: false },
    { key: 'pitch',        label: 'Additional Info',       type: 'textarea', placeholder: 'Insurance, ratings, certifications…' },
  ];

  if (jt === 'moving') return [
    { key: 'vehicle',      label: 'Truck / Vehicle',       type: 'text',     placeholder: 'e.g. 16 ft box truck, pickup…', required: false },
    { key: 'crewSize',     label: 'Crew (incl. you)',      type: 'stepper',  min: 1, max: 10, defaultVal: 2 },
    { key: 'movingExp',    label: 'Moving Jobs Done',      type: 'select',   options: ['First time', '1–5 moves', '5–20 moves', '20+ moves'], required: false },
    { key: 'heavyItems',   label: 'Can Handle Heavy Items (piano, safe…)', type: 'toggle' },
    { key: 'availability', label: 'Available Date',        type: 'date',     required: false },
    { key: 'pitch',        label: 'Additional Info',       type: 'textarea', placeholder: 'Equipment, insurance, notes…' },
  ];

  if (jt === 'delivery') return [
    { key: 'vehicle',      label: 'Delivery Vehicle',      type: 'select',   options: ['Bicycle', 'Motorcycle', 'Car', 'Van', 'Truck'], required: false },
    { key: 'deliveryExp',  label: 'Delivery Experience',   type: 'select',   options: ['New to delivery', '1–2 years', '3–5 years', '5+ years'], required: false },
    { key: 'coverageArea', label: 'Coverage Area',         type: 'text',     placeholder: 'Cities / zip codes you cover…', required: false },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'Days & hours…', required: false },
    { key: 'pitch',        label: 'Notes',                 type: 'textarea', placeholder: 'Insurance, max distance, rates…' },
  ];

  if (jt === 'home_repair') return [
    { key: 'specialty',    label: 'Trade Specialty',       type: 'text',     placeholder: 'Plumbing, electrical, carpentry…', required: false },
    { key: 'yearsExp',     label: 'Years of Experience',   type: 'select',   options: ['< 1 year', '1–3 years', '3–7 years', '7–15 years', '15+ years'], required: false },
    { key: 'licensed',     label: 'Licensed & Insured',    type: 'toggle' },
    { key: 'tools',        label: 'Tools / Equipment',     type: 'textarea', placeholder: 'Tools you own relevant to this job…' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'When can you start?', required: false },
    { key: 'pitch',        label: 'Why You\'re a Good Fit', type: 'textarea', placeholder: 'Past projects, qualifications…' },
  ];

  if (jt === 'cleaning') return [
    { key: 'yearsExp',     label: 'Cleaning Experience',   type: 'select',   options: ['< 1 year', '1–3 years', '3–7 years', '7+ years'], required: false },
    { key: 'propertyTypes', label: 'Property Types Handled', type: 'chip-select', options: ['Houses', 'Apartments', 'Offices', 'Commercial'], required: false },
    { key: 'suppliesProv', label: 'Supplies / Equipment Provided', type: 'toggle' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'Days & hours…', required: false },
    { key: 'pitch',        label: 'Additional Info',       type: 'textarea', placeholder: 'Past clients, references, certifications…' },
  ];

  if (jt === 'tutoring') return [
    { key: 'subjects',     label: 'Subject Expertise',     type: 'text',     placeholder: 'Math, Physics, SAT Prep…', required: false },
    { key: 'eduLevel',     label: 'Your Education Level',  type: 'select',   options: ['High School', 'Some College', 'Bachelor\'s', 'Master\'s', 'PhD', 'Professional'], required: false },
    { key: 'teachingExp',  label: 'Teaching Experience',   type: 'select',   options: ['None', '< 1 year', '1–3 years', '3–7 years', '7+ years'], required: false },
    { key: 'sessionMode',  label: 'Session Mode',          type: 'select',   options: ['In-Person', 'Online', 'Both'], required: false },
    { key: 'availability', label: 'Availability',          type: 'textarea', placeholder: 'Days & times you\'re free…', required: false },
    { key: 'qualifications', label: 'Qualifications / Certs', type: 'textarea', placeholder: 'Degrees, certifications, awards…' },
  ];

  if (jt === 'pet_care') return [
    { key: 'petTypes',     label: 'Pet Types You Handle',  type: 'text',     placeholder: 'Dogs, cats, birds…', required: false },
    { key: 'petExp',       label: 'Pet Care Experience',   type: 'select',   options: ['< 1 year', '1–3 years', '3–7 years', '7+ years'], required: false },
    { key: 'certifications', label: 'Certifications (optional)', type: 'text', placeholder: 'Pet first aid, grooming cert…' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'Days & hours…', required: false },
    { key: 'pitch',        label: 'Additional Info',       type: 'textarea', placeholder: 'References, past clients…' },
  ];

  if (jt === 'beauty') return [
    { key: 'specialty',    label: 'Specialty',             type: 'text',     placeholder: 'Hair, nails, makeup, lashes…', required: false },
    { key: 'yearsExp',     label: 'Years of Experience',   type: 'select',   options: ['< 1 year', '1–3 years', '3–7 years', '7+ years'], required: false },
    { key: 'licensed',     label: 'Licensed',              type: 'toggle' },
    { key: 'portfolio',    label: 'Portfolio / Instagram', type: 'text',     placeholder: '@handle or link…' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'When are you available?', required: false },
    { key: 'pitch',        label: 'Additional Info',       type: 'textarea', placeholder: 'Products you use, specialties…' },
  ];

  if (jt === 'tech') return [
    { key: 'specialty',    label: 'Tech Specialties',      type: 'text',     placeholder: 'iOS repair, networking, cybersecurity…', required: false },
    { key: 'yearsExp',     label: 'Years of Experience',   type: 'select',   options: ['< 1 year', '1–3 years', '3–7 years', '7+ years'], required: false },
    { key: 'certifications', label: 'Certifications',      type: 'text',     placeholder: 'CompTIA, Apple, Microsoft…' },
    { key: 'serviceMode',  label: 'Service Method',        type: 'chip-select', options: ['Remote / Online', 'On-Site', 'Drop-Off'], required: false },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'When can you help?', required: false },
    { key: 'pitch',        label: 'Additional Info',       type: 'textarea', placeholder: 'Tools, past projects, rates…' },
  ];

  if (jt === 'events') return [
    { key: 'eventTypes',   label: 'Event Types Covered',   type: 'text',     placeholder: 'Weddings, birthdays, corporate…', required: false },
    { key: 'yearsExp',     label: 'Years of Experience',   type: 'select',   options: ['< 1 year', '1–3 years', '3–7 years', '7+ years'], required: false },
    { key: 'equipment',    label: 'Equipment / Services Provided', type: 'textarea', placeholder: 'Lights, sound, photography, catering…', required: false },
    { key: 'portfolio',    label: 'Portfolio / Website',   type: 'text',     placeholder: 'Link to past events…' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'When are you available?', required: false },
  ];

  if (jt === 'handyman') return [
    { key: 'skills',       label: 'Skills / Specialties',  type: 'text',     placeholder: 'Carpentry, painting, plumbing…', required: false },
    { key: 'yearsExp',     label: 'Years of Experience',   type: 'select',   options: ['< 1 year', '1–3 years', '3–7 years', '7+ years'], required: false },
    { key: 'licensed',     label: 'Licensed & Insured',    type: 'toggle' },
    { key: 'tools',        label: 'Tools Owned',           type: 'textarea', placeholder: 'Power tools, ladders, etc…' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'When can you start?', required: false },
    { key: 'pitch',        label: 'Why You\'re a Good Fit', type: 'textarea', placeholder: 'Past work, references…' },
  ];

  // Generic catch-all for any other jobType
  return [
    { key: 'experience',   label: 'Relevant Experience',   type: 'textarea', placeholder: 'Describe your experience for this type of job…', required: false },
    { key: 'qualifications', label: 'Qualifications / Certs', type: 'text', placeholder: 'Degrees, licenses, certifications…' },
    { key: 'availability', label: 'Availability',          type: 'text',     placeholder: 'When are you available?', required: false },
    { key: 'pitch',        label: 'Why You\'re a Good Fit', type: 'textarea', placeholder: 'What makes you the right person for this?' },
  ];
}

// ── Default booking field templates per job type (customer-facing) ───────────
// Sellers configure which of these fields customers must fill when booking.
export function getJobTypeBookingTemplate(jobType) {
  const jt = jobType;

  if (jt === 'rides') return [
    { key: 'pickupLocation',   label: 'Pickup Location',   type: 'text',          placeholder: '123 Main St, City…',           required: false },
    { key: 'dropoffLocation',  label: 'Drop-off Location', type: 'text',          placeholder: 'Destination address…',         required: false },
    { key: 'rideDateTime',     label: 'Date & Time',       type: 'datetime-local',                                              required: false },
    { key: 'passengers',       label: 'Passengers',        type: 'stepper',       min: 1, max: 8, defaultVal: 1 },
    { key: 'notes',            label: 'Notes',             type: 'textarea',      placeholder: 'Special requests…' },
  ];

  if (jt === 'moving') return [
    { key: 'pickupLocation',  label: 'Moving From',   type: 'text',     placeholder: 'Current address…',        required: false },
    { key: 'dropoffLocation', label: 'Moving To',     type: 'text',     placeholder: 'New address…',            required: false },
    { key: 'moveDate',        label: 'Move Date',     type: 'date',                                             required: false },
    { key: 'estimatedLoad',   label: 'Load Size',     type: 'select',   options: ['Studio / 1BR', '2BR', '3BR', '4BR+', 'Office / Commercial'], required: false },
    { key: 'hasHeavyItems',   label: 'Has Heavy Items (piano, safe…)', type: 'toggle' },
    { key: 'notes',           label: 'Notes',         type: 'textarea', placeholder: 'Special instructions…' },
  ];

  if (jt === 'delivery') return [
    { key: 'pickupLocation',     label: 'Pickup Location',        type: 'text',          placeholder: 'Where to pick up…',  required: false },
    { key: 'dropoffLocation',    label: 'Delivery Address',       type: 'text',          placeholder: 'Deliver to…',        required: false },
    { key: 'deliveryByDateTime', label: 'Needed By',              type: 'datetime-local',                                   required: false },
    { key: 'itemDescription',    label: "What's Being Delivered", type: 'text',          placeholder: 'Item description…',  required: false },
    { key: 'fragile',            label: 'Fragile Item',           type: 'toggle' },
    { key: 'notes',              label: 'Notes',                  type: 'textarea',      placeholder: 'Special handling…' },
  ];

  if (jt === 'home_repair') return [
    { key: 'serviceAddress',  label: 'Service Address',       type: 'text',          placeholder: 'Your address…',               required: false },
    { key: 'serviceDateTime', label: 'Preferred Date & Time', type: 'datetime-local',                                            required: false },
    { key: 'jobScope',        label: 'Describe the Job',      type: 'textarea',      placeholder: "What needs fixing? Be specific…", required: false },
    { key: 'urgency',         label: 'Urgency',               type: 'select',        options: ['Flexible', 'This Week', 'ASAP'],   required: false },
  ];

  if (jt === 'cleaning') return [
    { key: 'serviceAddress',  label: 'Service Address',  type: 'text',          placeholder: 'Your address…',  required: false },
    { key: 'serviceDateTime', label: 'Date & Time',      type: 'datetime-local',                               required: false },
    { key: 'propertyType',    label: 'Property Type',    type: 'select',        options: ['House', 'Apartment', 'Condo', 'Office', 'Commercial'], required: false },
    { key: 'bedrooms',        label: 'Bedrooms',         type: 'stepper',       min: 0, max: 10, defaultVal: 2 },
    { key: 'bathrooms',       label: 'Bathrooms',        type: 'stepper',       min: 1, max: 10, defaultVal: 1 },
    { key: 'notes',           label: 'Notes',            type: 'textarea',      placeholder: 'Areas to focus on…' },
  ];

  if (jt === 'tutoring') return [
    { key: 'subject',           label: 'Subject',       type: 'text',     placeholder: 'Math, SAT Prep, Piano…', required: false },
    { key: 'gradeLevel',        label: 'Grade / Level', type: 'text',     placeholder: 'e.g. High School, College…', required: false },
    { key: 'sessionMode',       label: 'Session Mode',  type: 'select',   options: ['In-Person', 'Online', 'Either'], required: false },
    { key: 'preferredSchedule', label: 'Availability',  type: 'textarea', placeholder: "Days & times you're free…", required: false },
  ];

  if (jt === 'pet_care') return [
    { key: 'petType',         label: 'Pet Type',         type: 'text',          placeholder: 'Dog, Cat, Bird…',       required: false },
    { key: 'petBreed',        label: 'Breed (optional)', type: 'text',          placeholder: 'e.g. Golden Retriever…' },
    { key: 'serviceDateTime', label: 'Date & Time',      type: 'datetime-local',                                       required: false },
    { key: 'specialNeeds',    label: 'Special Needs',    type: 'textarea',      placeholder: 'Medications, allergies, behavior…' },
  ];

  if (jt === 'beauty') return [
    { key: 'serviceLocation', label: 'Location',        type: 'select',        options: ['At Your Studio', 'At My Location'], required: false },
    { key: 'serviceAddress',  label: 'Your Address (if at your location)', type: 'text', placeholder: 'Your address…' },
    { key: 'serviceDateTime', label: 'Date & Time',     type: 'datetime-local',                                   required: false },
    { key: 'notes',           label: 'Style Notes',     type: 'textarea',      placeholder: "What are you looking for? References…" },
  ];

  if (jt === 'tech') return [
    { key: 'deviceType',       label: 'Device',             type: 'text',     placeholder: 'iPhone 14, Dell laptop…',  required: false },
    { key: 'issueDescription', label: 'Describe the Issue', type: 'textarea', placeholder: "What's happening? Any error messages?", required: false },
    { key: 'serviceMode',      label: 'Service Method',     type: 'select',   options: ['Remote / Online', 'Drop-Off', 'On-Site Visit'], required: false },
    { key: 'urgency',          label: 'Urgency',            type: 'select',   options: ['Flexible', 'Within a Week', 'ASAP'], required: false },
  ];

  if (jt === 'events') return [
    { key: 'eventAddress', label: 'Event Location',  type: 'text',     placeholder: 'Venue or address…',             required: false },
    { key: 'eventDate',    label: 'Event Date',      type: 'date',                                                   required: false },
    { key: 'eventType',    label: 'Event Type',      type: 'text',     placeholder: 'Wedding, Birthday, Corporate…', required: false },
    { key: 'guestCount',   label: 'Expected Guests', type: 'stepper',  min: 1, max: 50000, defaultVal: 50 },
    { key: 'notes',        label: 'Details',         type: 'textarea', placeholder: "Services needed, special requirements…" },
  ];

  if (jt === 'handyman') return [
    { key: 'serviceAddress',  label: 'Service Address',       type: 'text',          placeholder: 'Your address…',          required: false },
    { key: 'serviceDateTime', label: 'Preferred Date & Time', type: 'datetime-local',                                       required: false },
    { key: 'jobScope',        label: 'Describe the Job',      type: 'textarea',      placeholder: "What needs to be done?", required: false },
    { key: 'urgency',         label: 'Urgency',               type: 'select',        options: ['Flexible', 'This Week', 'ASAP'], required: false },
  ];

  // Generic catch-all
  return [
    { key: 'serviceAddress',  label: 'Location', type: 'text',     placeholder: 'Address…' },
    { key: 'serviceDateTime', label: 'Date & Time',              type: 'datetime-local' },
    { key: 'jobScope',        label: 'Describe Your Request',    type: 'textarea', placeholder: "", required: false },
  ];
}

// ── Customer booking fields (serviceTab === 'offer', isService) ─────────────
// ── + Product attribute selectors ──────────────────────────────────────────
export function getItemFormFields(item) {
  if (!item) return [];

  // ── Gig claim (pro applying for a contract) ────────────────────────────────
  if (item.serviceTab === 'need') return getGigClaimFields(item);

  const jt = item.jobType;

  // ── Offer service: use seller-configured booking form if set, else template ─
  if (jt && item.serviceTab === 'offer') {
    const saved = item.bookingForm?.fields;
    if (saved?.length > 0) return saved.filter(f => f.enabled !== false);
    return getJobTypeBookingTemplate(jt);
  }

  // ── Service with jobType but no serviceTab set ──────────────────────────────
  if (jt) return getJobTypeBookingTemplate(jt);

  // Duration (the actual driver of an hourly/daily service's price) is rendered
  // as its own dedicated control in the order card rather than folded into this
  // generic field list — see the options editor in UnifiedCartWindow.
  if (item.isService) return [
    { key: 'serviceDateTime', label: 'Preferred Date & Time', type: 'datetime-local', required: false },
    { key: 'notes',           label: 'Notes',                 type: 'textarea',       placeholder: 'Any specific requests…' },
  ];

  // ── Regular product — attribute selectors + restaurant customOptions ───────
  const fields = [];
  (item.attributes || []).forEach(attr => {
    const vals = (attr.values || []).filter(Boolean);
    if (attr.name && vals.length > 0) {
      fields.push({
        key: `attr_${attr.name.toLowerCase().replace(/\s+/g, '_')}`,
        label: attr.name,
        type: 'chip-select',
        options: vals,
        required: true,
      });
    }
  });
  const customOpts = (item.customOptions || []).filter(Boolean);
  if (customOpts.length > 0) {
    fields.push({ key: 'customOption', label: 'Choose Option', type: 'chip-select', options: customOpts, required: true });
  }
  if (fields.length > 0) {
    fields.push({ key: 'notes', label: 'Special Instructions', type: 'textarea', placeholder: 'Allergies, preferences…' });
  }
  return fields;
}

const FIELD_LABELS = {
  // Customer booking
  pickupLocation: 'Pickup', dropoffLocation: 'Drop-off', rideDateTime: 'Date/Time',
  passengers: 'Passengers', moveDate: 'Move Date', estimatedLoad: 'Load Size',
  hasHeavyItems: 'Heavy Items', deliveryByDateTime: 'Needed By', itemDescription: 'Item',
  fragile: 'Fragile', serviceAddress: 'Address', serviceDateTime: 'Date/Time',
  jobScope: 'Job Description', urgency: 'Urgency', propertyType: 'Property Type',
  bedrooms: 'Bedrooms', bathrooms: 'Bathrooms', subject: 'Subject', gradeLevel: 'Level',
  sessionMode: 'Mode', preferredSchedule: 'Availability', petType: 'Pet', petBreed: 'Breed',
  specialNeeds: 'Special Needs', serviceLocation: 'Location', deviceType: 'Device',
  issueDescription: 'Issue', serviceMode: 'Service Method', eventAddress: 'Event Venue',
  eventDate: 'Event Date', eventType: 'Event Type', guestCount: 'Guests',
  customOption: 'Option', notes: 'Notes',
  // Gig claim (pro)
  vehicle: 'Vehicle', seats: 'Seats', drivingExp: 'Driving Experience', licensed: 'Licensed',
  crewSize: 'Crew Size', movingExp: 'Moving Experience', heavyItems: 'Heavy Items OK',
  deliveryExp: 'Delivery Experience', coverageArea: 'Coverage Area',
  specialty: 'Specialty', yearsExp: 'Experience', tools: 'Tools/Equipment',
  suppliesProv: 'Supplies Provided', propertyTypes: 'Property Types',
  subjects: 'Subjects', eduLevel: 'Education', teachingExp: 'Teaching Experience',
  qualifications: 'Qualifications', petTypes: 'Pet Types', petExp: 'Pet Experience',
  certifications: 'Certifications', portfolio: 'Portfolio', eventTypes: 'Event Types',
  equipment: 'Equipment/Services', skills: 'Skills', experience: 'Experience',
  availability: 'Availability', pitch: 'About Me', serviceMode: 'Service Mode',
  durationHours: 'Duration', pickupDate: 'Pick-up Date', dropoffDate: 'Drop-off Date',
  durationDays: 'Days',
};

// Computed pricing metadata carried on formData for the total-price calc — not
// customer-entered fields, so they're excluded from the chat-facing details message.
const NON_DISPLAY_KEYS = new Set(['pricingType', 'totalPrice']);

const titleCase = (s) => s.replace(/\b\w/g, c => c.toUpperCase());

// Product attribute fields are keyed `attr_${attributeName}` (see getItemFormFields
// below) — recover a clean label like "Size" from `attr_size` / `attr_shoe_size`.
const labelForKey = (k) => {
  if (FIELD_LABELS[k]) return FIELD_LABELS[k];
  if (k === 'customOption') return 'Option';
  if (k.startsWith('attr_')) return titleCase(k.slice(5).replace(/_/g, ' '));
  return titleCase(k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim());
};

// Structured [{ key, label, value }] rows for the buyer-submitted order form —
// used to render each field on its own line (e.g. in the order card UI) rather
// than as a single flattened block of text.
export function getOrderDetailsEntries(formData) {
  if (!formData || Object.keys(formData).length === 0) return [];
  return Object.entries(formData)
    .filter(([k, v]) => !NON_DISPLAY_KEYS.has(k) && v !== '' && v !== null && v !== undefined)
    .map(([k, v]) => {
      if (typeof v === 'boolean') {
        return v ? { key: k, label: labelForKey(k), value: 'Yes' } : null;
      }
      const value = k === 'durationHours' ? `${v} hr${v === 1 ? '' : 's'}` : v;
      return { key: k, label: labelForKey(k), value };
    })
    .filter(Boolean);
}

export function formatOrderDetailsMessage(formData) {
  return getOrderDetailsEntries(formData)
    .map(({ label, value }) => `• ${label}: ${value}`)
    .join('\n');
}
